import { Col, Form, Modal, Radio, Row, Space } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React, { useContext, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { dokumentZalozeniApi } from 'services/dokumenty/smlouva';

import {
    EPodTypDokumentu,
    EPodTypdokumentuPrikazZapis,
    EPodTypdokumentuPrikazZmena,
    EPodTypdokumentuPrikazZruseni,
    ETypDokumentu
} from 'enums/dokumentyEnums';

import getLanguageObject from 'utils/getLanguageObject';

interface IRozcestnik {
    nextStep: () => void;
    setZadostID: (value: number) => void;
}

const filterByEnum = <T extends Record<string, string | number>>(
    values: { typprikazu: string }[],
    enumType: T,
    language: string
) => {
    const enumValues = new Set(Object.values(enumType));

    return values
        .filter(e => enumValues.has(e.typprikazu as T[keyof T]))
        .map(e => ({
            value:
                e.typprikazu === 'PREVODITELNOST' && (enumType as unknown) === EPodTypdokumentuPrikazZapis
                    ? 'PREVODITELNOST_ALT'
                    : e.typprikazu,
            labelcz:
                e.typprikazu === 'PREVODITELNOST' && (enumType as unknown) === EPodTypdokumentuPrikazZapis
                    ? 'DOKUMENTY_PRIKAZ_EXTRASHORT_PREVODITELNOST_ALT'
                    : `DOKUMENTY_PRIKAZ_EXTRASHORT_${e.typprikazu}`
        }))
        .sort((a, b) => {
            const aLabel = getLanguageObject(language)[a.labelcz];
            const bLabel = getLanguageObject(language)[b.labelcz];
            if (aLabel < bLabel) return -1;
            if (aLabel > bLabel) return 1;
            return 0;
        });
};

const Rozcestnik: React.FC<IRozcestnik> = ({ nextStep, setZadostID }) => {
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const { aktivniFirma, dokumenty, language } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [modalErr, setModalErr] = useState<string>('');
    const prikazyZapis = filterByEnum(dokumenty?.dokumentyPrava?.prikazy || [], EPodTypdokumentuPrikazZapis, language);
    const prikazyZmena = filterByEnum(dokumenty?.dokumentyPrava?.prikazy || [], EPodTypdokumentuPrikazZmena, language);
    const prikazyZruseni = filterByEnum(
        dokumenty?.dokumentyPrava?.prikazy || [],
        EPodTypdokumentuPrikazZruseni,
        language
    );

    const onFinish = (value: Store) => {
        setLoading(true);
        dokumentZalozeniApi({
            typ: value.typ,
            podtyp: value.podtyp === 'PREVODITELNOST_ALT' ? 'PREVODITELNOST' : value.podtyp, // || value.podtypZapis || value.podtypZmena || value.podtypZruseni,
            ...(value.isin && {
                isin: value.isin
            })
        })
            .then(resp => {
                if (resp.stav !== 'OK') {
                    setModalErr(resp.stav);
                } else {
                    setZadostID(resp.id);
                    nextStep();
                    setLevel('dokumenty', {
                        ...dokumenty,
                        selectedZadost: {
                            iddok: resp.id,
                            typ: value.typ,
                            podtyp: value.podtyp === 'PREVODITELNOST_ALT' ? 'PREVODITELNOST' : value.podtyp, // || value.podtypZapis || value.podtypZmena || value.podtypZruseni,
                            ...(value.isin && {
                                isin: value.isin
                            })
                        }
                    });
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish}>
                <InputComponent
                    disabled={true}
                    initialValue={aktivniFirma?.ic}
                    label="IDENTIFIKACNI_CISLO"
                    name="idmb"
                    notRequired={true}
                />
                <InputComponent
                    disabled={true}
                    initialValue={(aktivniFirma?.spolecnost || '').split('  ').join(' ')}
                    label="ISIN_IDENTIFIKACE_FIRMA"
                    name="spolecnost"
                    notRequired={true}
                    rules={[]}
                />
                <SelectComponent
                    isFormItem={true}
                    data={[
                        ...(!dokumenty?.dokumentyPrava?.smlouva &&
                        !dokumenty?.dokumentyPrava?.smlouvadlt &&
                        !dokumenty?.dokumentyPrava?.dodatek?.length &&
                        !dokumenty?.dokumentyPrava?.dodatekdlt?.length &&
                        !dokumenty?.dokumentyPrava?.navyseni?.length &&
                        !dokumenty?.dokumentyPrava?.navysenidlt?.length
                            ? []
                            : [
                                  {
                                      value: ETypDokumentu.SMLOUVY,
                                      labelcz: 'TYP_DOKUMENTU_SMLUVA'
                                  }
                              ]),
                        ...(dokumenty?.dokumentyPrava?.prikazy?.length
                            ? [
                                  {
                                      value: ETypDokumentu.PRIKAZY,
                                      labelcz: 'TYP_DOKUMENTU_PRIKAZ'
                                  }
                              ]
                            : [])
                    ]}
                    name="typ"
                    labelParagraph={'TYP_DOKUMENTU'}
                    onChange={() => {
                        rerender(pv => !pv);
                        form.resetFields(['podtyp', 'isin']);
                    }}
                />
                {form.getFieldValue('typ') === ETypDokumentu.SMLOUVY && (
                    <SelectComponent
                        isFormItem={true}
                        data={(form.getFieldValue('typ') === ETypDokumentu.SMLOUVY
                            ? [
                                  ...(dokumenty?.dokumentyPrava?.smlouva
                                      ? [
                                            {
                                                value: EPodTypDokumentu.SMLOUVA,
                                                labelcz: 'TYP_SMLOUVY_SMLOUVA'
                                            }
                                        ]
                                      : []),
                                  ...(dokumenty?.dokumentyPrava?.smlouvadlt
                                      ? [
                                            {
                                                value: EPodTypDokumentu.SMLOUVADLT,
                                                labelcz: 'TYP_SMLOUVY_SMLOUVADLT'
                                            }
                                        ]
                                      : []),
                                  ...(dokumenty?.dokumentyPrava?.dodatek?.length
                                      ? [
                                            {
                                                value: EPodTypDokumentu.DODATEK,
                                                labelcz: 'TYP_SMLOUVY_DODATEK'
                                            }
                                        ]
                                      : []),
                                  ...(dokumenty?.dokumentyPrava?.dodatekdlt?.length
                                      ? [
                                            {
                                                value: EPodTypDokumentu.DODATEKDLT,
                                                labelcz: 'TYP_SMLOUVY_DODATEKDLT'
                                            }
                                        ]
                                      : []),
                                  ...(dokumenty?.dokumentyPrava?.navyseni?.length
                                      ? [
                                            {
                                                value: EPodTypDokumentu.NAVYSENI,
                                                labelcz: 'TYP_SMLOUVY_NAVYSENI'
                                            }
                                        ]
                                      : []),
                                  ...(dokumenty?.dokumentyPrava?.navysenidlt?.length
                                      ? [
                                            {
                                                value: EPodTypDokumentu.NAVYSENIDLT,
                                                labelcz: 'TYP_SMLOUVY_NAVYSENIDLT'
                                            }
                                        ]
                                      : [])
                              ]
                            : []
                        ).sort((a, b) => {
                            const aLabel = getLanguageObject(language)[a.labelcz];
                            const bLabel = getLanguageObject(language)[b.labelcz];
                            if (aLabel < bLabel) return -1;
                            if (aLabel > bLabel) return 1;
                            return 0;
                        })}
                        name="podtyp"
                        labelParagraph={
                            form.getFieldValue('typ') === ETypDokumentu.SMLOUVY ? 'TYP_SMLOUVY' : 'DOKUMENTY_PRIKAZ'
                        }
                        onChange={() => rerender(pv => !pv)}
                    />
                )}
                {form.getFieldValue('typ') === ETypDokumentu.PRIKAZY && (
                    <Form.Item
                        name="podtyp"
                        rules={[
                            {
                                required: true,
                                message: <TranslateText>DOKUMENTY_PRIKAZ_ERR</TranslateText>
                            }
                        ]}
                        style={{
                            paddingBottom: 20
                        }}
                    >
                        <Radio.Group
                            style={{
                                width: '100%',
                                marginBottom: 30
                            }}
                            onChange={() => rerender(prev => !prev)}
                        >
                            <Row gutter={[24, 24]}>
                                {!!prikazyZapis.length && (
                                    <Col xs={24} md={8}>
                                        <Paragraph
                                            style={{
                                                marginBottom: 6,
                                                display: 'block'
                                            }}
                                            text="DOKUMENTY_PRIKAZ_ZAPIS"
                                            strong={true}
                                        />
                                        <Space direction="vertical">
                                            {prikazyZapis.map(item => (
                                                <Radio key={item.value} value={item.value} id={item.value}>
                                                    <TranslateText>{item.labelcz}</TranslateText>
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Col>
                                )}

                                {!!prikazyZmena.length && (
                                    <Col xs={24} md={8}>
                                        <Paragraph
                                            style={{
                                                marginBottom: 6,
                                                display: 'block'
                                            }}
                                            text="DOKUMENTY_PRIKAZ_ZMENA"
                                            strong={true}
                                        />
                                        <Space direction="vertical">
                                            {prikazyZmena.map(item => (
                                                <Radio key={item.value} value={item.value} id={item.value}>
                                                    <TranslateText>{item.labelcz}</TranslateText>
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Col>
                                )}

                                {!!prikazyZruseni.length && (
                                    <Col xs={24} md={8}>
                                        <Paragraph
                                            style={{
                                                marginBottom: 6,
                                                display: 'block'
                                            }}
                                            text="DOKUMENTY_PRIKAZ_ZRUSE"
                                            strong={true}
                                        />
                                        <Space direction="vertical">
                                            {prikazyZruseni.map(item => (
                                                <Radio key={item.value} value={item.value} id={item.value}>
                                                    <TranslateText>{item.labelcz}</TranslateText>
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Col>
                                )}
                            </Row>
                        </Radio.Group>
                    </Form.Item>
                )}
                {(form.getFieldValue('podtyp') === EPodTypDokumentu.DODATEK ||
                    form.getFieldValue('podtyp') === EPodTypDokumentu.DODATEKDLT ||
                    form.getFieldValue('podtyp') === EPodTypDokumentu.NAVYSENIDLT ||
                    form.getFieldValue('podtyp') === EPodTypDokumentu.NAVYSENI) && (
                    <SelectComponent
                        isFormItem={true}
                        data={(
                            (form.getFieldValue('podtyp') === EPodTypDokumentu.DODATEK
                                ? dokumenty?.dokumentyPrava?.dodatek
                                : form.getFieldValue('podtyp') === EPodTypDokumentu.DODATEKDLT
                                  ? dokumenty?.dokumentyPrava?.dodatekdlt
                                  : form.getFieldValue('podtyp') === EPodTypDokumentu.NAVYSENIDLT
                                    ? dokumenty?.dokumentyPrava?.navysenidlt
                                    : form.getFieldValue('podtyp') === EPodTypDokumentu.NAVYSENI
                                      ? dokumenty?.dokumentyPrava?.navyseni
                                      : []) || []
                        ).map(e => {
                            return {
                                labelcz: e.isin,
                                value: e.isin
                            };
                        })}
                        name="isin"
                        labelParagraph={'ISIN'}
                    />
                )}
                <Row>
                    <Col>
                        <ButtonComponent buttontext="POKRACOVAT" onClick={() => form.submit()} id="isinPokracovatBtn" />
                    </Col>
                </Row>
            </FormWrapper>
            <Modal
                title={<TranslateText>{'NOVY_DOKUMNET'}</TranslateText>}
                open={!!modalErr}
                onCancel={() => setModalErr('')}
                afterClose={() => {
                    setModalErr('');
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalErr('');
                            }}
                            id="emitentPrehledZavritBtn"
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={'DOKUMENT_NOVY_ERR_' + modalErr} />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default Rozcestnik;
